/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import moment from "moment";
import React from "react";
import Datetime from "react-datetime";
import ReactDOM from "react-dom";
import styled, { createGlobalStyle } from "styled-components";

import { TextInputClose } from "@client/components/ui/TextInput";
import DateUtils from "@client/utils/DateUtils";
import DomUtils from "@client/utils/DomUtils";
import StyleUtils from "@client/utils/Style";

import DropdownButton from "../DropdownButton";
import style from "./style";

require("moment/locale/en-gb");

const GlobalStyle = createGlobalStyle`${style}`;

const Wrapper = styled.div<{ width?: number }>`
  position: relative;
  width: ${props => props.width || StyleUtils.inputSizes.regular.width}px;
`;
const DropdownButtonStyled = styled(DropdownButton)`
  font-size: 12px;
`;
const Portal = styled.div`
  position: absolute;
  z-index: 9999;
  transition: all 200ms cubic-bezier(0.3, 1, 0.4, 1) 0s;
  &.hideTip {
    .rdtPicker:after {
      content: none;
    }
  }
`;
type DateElProps = {
  style: React.CSSProperties;
  onViewModeChange: () => void;
};
const DatetimeStyled = styled(Datetime)<DateElProps>`
  ${StyleUtils.boxShadow}
`;

type Props = {
  value: Date | null;
  onChange: (date: Date) => void;
  isValidDate?: (currentDate: Date, selectedDate: Date) => boolean;
  timezone: "utc" | "local";
  useBold?: boolean;
  dispatchChangeContinously?: boolean;
  width?: number;
  disabled?: boolean;
  highlight?: boolean;
  monthMode?: boolean;
  emptyDateLabel?: string;
  dateFormat?: string;
  showClearButton?: boolean;
  onClear?: () => void;
};
type State = {
  showPicker: boolean;
  date: any | null;
};
@observer
class DatetimePicker extends React.Component<Props, State> {
  state: State = {
    showPicker: false,
    date: null,
  };

  itemMouseDown = false;

  portalRef: HTMLElement | null = null;

  buttonRef: HTMLElement | null = null;

  scrollableParent: HTMLElement | null = null;

  UNSAFE_componentWillMount() {
    if (this.props.value) {
      this.setState({ date: moment(this.props.value) });
    }
  }

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
    if (this.buttonRef) {
      this.scrollableParent = DomUtils.getScrollableParent(
        this.buttonRef,
      ) as HTMLElement;
      this.scrollableParent.addEventListener("scroll", this.handleScroll);
    }
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    this.setState({ date: newProps.value && moment(newProps.value) });
  }

  componentDidUpdate() {
    this.setPortalPosition();
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
    if (this.scrollableParent) {
      this.scrollableParent.removeEventListener(
        "scroll",
        this.handleScroll,
        false,
      );
    }
  }

  setPortalPosition() {
    const portalRef = this.portalRef;
    const buttonRef = this.buttonRef;
    if (!portalRef || !buttonRef) {
      return;
    }

    const buttonRect = buttonRef.getBoundingClientRect();
    const leftOffset =
      buttonRect.left - (portalRef.offsetWidth - buttonRect.width) + 10;
    const tipHeight = 12;
    let topOffset = buttonRect.top + buttonRef.offsetHeight + tipHeight;
    const listHeight = portalRef.offsetHeight;

    if (topOffset + listHeight > window.innerHeight) {
      topOffset = window.innerHeight - listHeight - 16;
      portalRef.classList.add("hideTip");
    } else {
      portalRef.classList.remove("hideTip");
    }

    portalRef.style.top = `${topOffset + window.pageYOffset}px`;
    portalRef.style.left = `${leftOffset + window.pageXOffset}px`;
  }

  isValidDate(currentDate: Date, selectedDate: Date): boolean {
    if (!this.props.isValidDate) {
      return true;
    }

    return this.props.isValidDate(currentDate, selectedDate);
  }

  @autobind
  handleScroll() {
    if (this.buttonRef) {
      if (DomUtils.isElementInViewport(this.buttonRef, this.scrollableParent)) {
        this.setPortalPosition();
      } else if (this.state.showPicker) {
        this.setState({ showPicker: false });
      }
    }
  }

  @autobind
  handlePageClick(e: Event) {
    const path = DomUtils.getEventPath(e);

    if (!this.itemMouseDown && !path.find(n => n.className === "rdtPicker")) {
      this.dispatchChange();
      this.setState({ showPicker: false });
    }
  }

  handleDropdownClick() {
    this.dispatchChange();
    this.setState(prevState => ({ showPicker: !prevState.showPicker }));
  }

  handleChange(newDate: moment.Moment | string) {
    let date = moment(newDate);
    if (this.props.timezone === "utc") {
      date = DateUtils.getLocalTime(newDate);
    }

    this.setState({ date }, () => {
      if (this.props.dispatchChangeContinously) {
        this.dispatchChange();
      }
    });
  }

  dispatchChange() {
    if (
      this.state.date &&
      this.state.showPicker &&
      this.state.date.toDate().getTime() !==
        (this.props.value && this.props.value.getTime())
    ) {
      this.props.onChange(this.state.date.toDate());
    }
  }

  renderDateTimePicker(timezoneDate: any) {
    if (!this.state.showPicker) {
      return null;
    }

    const { body } = document;
    if (!body) {
      return null;
    }
    return ReactDOM.createPortal(
      <Portal
        ref={e => {
          this.portalRef = e;
        }}
      >
        <DatetimeStyled
          input={false}
          value={timezoneDate}
          style={{ top: 0, right: 0 }}
          onChange={date => {
            this.handleChange(date);
          }}
          onViewModeChange={() => {
            setTimeout(() => {
              this.setPortalPosition();
            }, 100);
          }}
          dateFormat={this.props.monthMode ? "MMMM YYYY" : "DD/MM/YYYY"}
          timeFormat={this.props.monthMode ? false : "hh:mm A"}
          initialViewMode={this.props.monthMode ? "months" : undefined}
          renderMonth={
            this.props.monthMode
              ? (props, month, year) => {
                  return (
                    <td
                      {...props}
                      onClick={e => {
                        e.preventDefault();
                        if (
                          !e.currentTarget.classList.contains("rdtDisabled")
                        ) {
                          this.handleChange(moment(new Date(year, month, 1)));
                        }
                      }}
                    >
                      {moment.monthsShort("MMM", month)}
                    </td>
                  );
                }
              : undefined
          }
          locale="en-gb"
          isValidDate={(currentDate: Date, selectedDate: Date) =>
            this.isValidDate(currentDate, selectedDate)
          }
        />
      </Portal>,
      body,
    );
  }

  render() {
    let timezoneDate = this.state.date;
    if (this.props.timezone === "utc" && timezoneDate) {
      timezoneDate = DateUtils.getUtcTime(timezoneDate);
    }

    return (
      <>
        <GlobalStyle />
        <Wrapper width={this.props.width}>
          <DropdownButtonStyled
            customRef={e => {
              this.buttonRef = e;
            }}
            disabled={this.props.disabled}
            width={this.props.width || 207}
            value={
              (timezoneDate &&
                moment(timezoneDate).format(
                  this.props.dateFormat || "DD/MM/YYYY hh:mm A",
                )) ||
              this.props.emptyDateLabel ||
              "-"
            }
            centered
            useBold={this.props.useBold}
            onClick={() => {
              this.handleDropdownClick();
            }}
            // @ts-ignore
            onMouseDown={() => {
              this.itemMouseDown = true;
            }}
            onMouseUp={() => {
              this.itemMouseDown = false;
            }}
          />
          {this.props.showClearButton ? (
            <TextInputClose
              show={timezoneDate && !this.props.disabled}
              style={{ right: "-24px" }}
              onClick={this.props.onClear}
            />
          ) : null}
          {this.renderDateTimePicker(timezoneDate)}
        </Wrapper>
      </>
    );
  }
}

export default DatetimePicker;
